import { useState, type ReactNode } from "react";
import "./QuoteBlock.css";
import { parseBlockContent } from "../../../lib/blockContentSchemas";
import type { BlockEditPatch, EditorBlock } from "../editor.types";
import { EditActions, EditField } from "./BlockEditControls";
import { editInputClass, onEscapeCancel } from "../editUtils";

type QuoteBlockProps = {
  block: EditorBlock;
  editing?: boolean;
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancelEdit?: () => void;
};

export const QuoteBlock = ({
  block,
  editing,
  onSave,
  onCancelEdit,
}: QuoteBlockProps): ReactNode => {
  const content = parseBlockContent("quote", block.content);

  if (editing) {
    return (
      <QuoteBlockEditor
        attribution={content.attribution}
        onCancel={onCancelEdit}
        onSave={onSave}
        text={content.text}
      />
    );
  }

  return (
    <blockquote className="editor-quote">
      {content.text}
      {content.attribution && (
        <cite className="mt-3 block font-site-body text-sm not-italic text-site-muted">
          — {content.attribution}
        </cite>
      )}
    </blockquote>
  );
};

type QuoteBlockEditorProps = {
  text: string;
  attribution: string;
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancel?: () => void;
};

const QuoteBlockEditor = ({
  text,
  attribution,
  onSave,
  onCancel,
}: QuoteBlockEditorProps) => {
  const [draftText, setDraftText] = useState(text);
  const [draftAttribution, setDraftAttribution] = useState(attribution);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    const result = await onSave?.({
      content: { text: draftText, attribution: draftAttribution },
    });
    setSaving(false);
    if (result) setError(result);
  };

  return (
    <div
      className="space-y-3"
      onKeyDown={onEscapeCancel(onCancel)}
    >
      <textarea
        autoFocus
        className={`${editInputClass} resize-y font-lora text-xl italic leading-8`}
        onChange={(event) => setDraftText(event.target.value)}
        rows={3}
        value={draftText}
      />
      <EditField label="Attribution">
        <input
          className={editInputClass}
          onChange={(event) => setDraftAttribution(event.target.value)}
          placeholder="Who said it?"
          value={draftAttribution}
        />
      </EditField>
      <EditActions
        error={error}
        onCancel={onCancel}
        onSave={save}
        saveDisabled={!draftText.trim()}
        saving={saving}
      />
    </div>
  );
};
